import {
  visibleAchievementCount,
  type AchievementView,
  type GlobalAchievementValues,
} from "@dailywars/core/achievements";

import { Progress } from "#/components/ui/progress";
import { cn } from "#/lib/utils";

const achievementNumberFormatter = new Intl.NumberFormat("en-US");

function formatAchievementProgress({
  current,
  target,
}: {
  current: number;
  target: number;
}): string {
  return `${achievementNumberFormatter.format(current)} / ${achievementNumberFormatter.format(target)}`;
}

function AchievementProgressBar({
  current,
  target,
  className,
  progressClassName,
  labelClassName,
  fillLabelClassName,
}: {
  current: number;
  target: number;
  className?: string;
  progressClassName?: string;
  labelClassName?: string;
  fillLabelClassName: string;
}) {
  const percentage = target <= 0 ? 0 : Math.min(100, Math.floor((current / target) * 100));
  const label = formatAchievementProgress({ current, target });
  return (
    <div className={cn("relative", className)}>
      <Progress value={percentage} className={cn("gap-0", progressClassName)} />
      <p
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center font-medium tabular-nums text-foreground",
          labelClassName,
        )}
      >
        {label}
      </p>
      <p
        aria-hidden
        className={cn(
          "pointer-events-none absolute inset-0 flex items-center justify-center font-medium tabular-nums",
          fillLabelClassName,
          labelClassName,
        )}
        style={{ clipPath: `inset(0 ${100 - percentage}% 0 0)` }}
      >
        {label}
      </p>
    </div>
  );
}

export function GlobalAchievementProgress({
  achievement,
  values,
}: {
  achievement: AchievementView;
  values: GlobalAchievementValues | undefined;
}) {
  if (
    achievement.earnedAt !== undefined ||
    !values ||
    achievement.condition.kind !== "global" ||
    achievement.condition.metric === "bestMedalTier"
  ) {
    return null;
  }

  const current = values[achievement.condition.metric];
  const target = achievement.condition.threshold;
  return (
    <AchievementProgressBar
      current={current}
      target={target}
      className="mt-3"
      progressClassName="[&_[data-slot=progress-track]]:h-5"
      labelClassName="text-xs"
      fillLabelClassName="text-primary-foreground"
    />
  );
}

export function OverallAchievementProgress({
  earnedCount,
  totalCount = visibleAchievementCount(),
}: {
  earnedCount: number;
  totalCount?: number;
}) {
  return (
    <AchievementProgressBar
      current={earnedCount}
      target={totalCount}
      progressClassName="[&_[data-slot=progress-track]]:h-8 [&_[data-slot=progress-indicator]]:bg-accent"
      labelClassName="text-sm"
      fillLabelClassName="text-accent-foreground"
    />
  );
}
